// New-clip screen — pick start/end markers on the full episode video,
// then create a manual reels_clip + auto-generate subtitles from transcript words.

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../state/store.js';
import { Icons, fmtClock, fmtDur, parseClock } from '../components/ui.jsx';

export function NewClipScreen() {
  const { epId } = useParams();
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);

  const [episode, setEpisode] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [duration, setDuration] = useState(0);
  const [markIn, setMarkIn] = useState(0);
  const [markOut, setMarkOut] = useState(60);
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const videoRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.getEpisode(epId)
      .then((ep) => {
        if (!alive) return;
        setEpisode(ep);
        const dur = ep.duration_seconds || 0;
        setDuration(dur);
        setMarkOut(Math.min(60, dur || 60));
      })
      .catch((err) => toast(err.message, 'err'));
    return () => { alive = false; };
  }, [epId, toast]);

  // Stop playback when crossing markOut while previewing.
  // MUST stay above the early returns — Rules of Hooks.
  useEffect(() => {
    if (!playing) return;
    if (playhead >= markOut && videoRef.current) {
      videoRef.current.pause();
    }
  }, [playhead, markOut, playing]);

  if (!episode) {
    return <div className="screen"><div className="empty-state"><div className="empty-icon">◇</div><div className="empty-title">загрузка эпизода…</div></div></div>;
  }
  if (episode.status !== 'ready') {
    return (
      <div className="screen">
        <div className="empty-state">
          <div className="empty-icon">⏳</div>
          <div className="empty-title">эпизод ещё обрабатывается</div>
          <div className="empty-sub">статус: {episode.status}{episode.status_message ? ' · ' + episode.status_message : ''}</div>
          <button className="btn btn-outline" style={{ marginTop: 16 }} onClick={() => navigate(`/episode/${epId}`)}>
            ← к транскрипту
          </button>
        </div>
      </div>
    );
  }

  const videoUrl = api.videoUrl(epId);
  // safeDuration avoids div-by-zero in timeline marker positioning when ffprobe
  // hasn't set duration_seconds yet (e.g. just after upload). Use video element's
  // own .duration as fallback once loaded.
  const safeDuration = Math.max(1, duration || episode.duration_seconds || 60);
  const clipDur = Math.max(0, markOut - markIn);

  const seek = (sec) => {
    if (!videoRef.current) return;
    const v = Math.max(0, Math.min(duration || sec, sec));
    try { videoRef.current.currentTime = v; setPlayhead(v); } catch {}
  };
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause(); else videoRef.current.play();
  };

  const setStartHere = () => setMarkIn(Math.min(playhead, markOut - 1));
  const setEndHere = () => setMarkOut(Math.max(playhead, markIn + 1));
  const previewRange = () => {
    seek(markIn);
    if (videoRef.current) videoRef.current.play();
  };

  const create = async () => {
    if (markOut <= markIn) { toast('конец должен быть после начала', 'warn'); return; }
    if (clipDur < 2) { toast('минимум 2 секунды', 'warn'); return; }
    if (clipDur > 180) {
      if (!window.confirm(`Клип ${fmtDur(clipDur)} — это больше 3 минут. Точно создаём?`)) return;
    }
    setCreating(true);
    setError(null);
    try {
      const clip = await api.createClip({
        episode_id: epId,
        t_start: markIn,
        t_end: markOut,
        title: title.trim() || undefined,
      });
      toast(`клип создан · ${fmtDur(clipDur)}`, 'ok');
      navigate(`/clip/${clip.id}`);
    } catch (err) {
      setError(err.message);
      toast(err.message, 'err');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="screen new-clip-screen">
      <div className="breadcrumb">
        <button className="back" onClick={() => navigate(`/episode/${epId}`)}>← к транскрипту</button>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">{episode.title}</span>
        <span className="crumb-sep">/</span>
        <span className="crumb-current crumb-strong">создать клип</span>
        <span className="chip chip-soft">{fmtClock(markIn)} → {fmtClock(markOut)} · {fmtDur(clipDur)}</span>
      </div>

      <div className="new-clip-layout">
        {/* LEFT: video + timeline */}
        <section className="new-clip-video">
          <div className="new-clip-frame">
            <video
              ref={videoRef}
              src={videoUrl}
              preload="auto"
              controls
              playsInline
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={() => videoRef.current && setPlayhead(videoRef.current.currentTime)}
              onLoadedMetadata={() => {
                if (!videoRef.current) return;
                if (!duration) setDuration(videoRef.current.duration || 0);
              }}
              onError={(e) => {
                const code = e.target?.error?.code;
                setError(`не удалось загрузить видео (код ${code}). Возможно, ингест ещё не завершил faststart-проход.`);
              }}
              style={{ width: '100%', maxHeight: '60vh', background: '#000', display: 'block', borderRadius: 12 }}
            />
          </div>

          {error && (
            <div className="error-banner" style={{ marginTop: 12, padding: '10px 14px', background: 'var(--err)', color: '#fff', borderRadius: 8 }}>
              ⚠ {error}
            </div>
          )}

          {/* Timeline with markers */}
          <div className="new-clip-timeline" style={{ marginTop: 16 }}>
            <div className="nc-scrub" onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
              seek(f * (duration || 1));
            }}>
              <div className="nc-scrub-range" style={{
                left: `${(markIn / safeDuration) * 100}%`,
                width: `${(clipDur / safeDuration) * 100}%`,
              }} />
              <div className="nc-scrub-knob" style={{ left: `${(playhead / safeDuration) * 100}%` }} title="текущее время" />
              <div className="nc-scrub-mark nc-mark-in" style={{ left: `${(markIn / safeDuration) * 100}%` }} title={`начало ${fmtClock(markIn)}`} />
              <div className="nc-scrub-mark nc-mark-out" style={{ left: `${(markOut / safeDuration) * 100}%` }} title={`конец ${fmtClock(markOut)}`} />
            </div>
            <div className="scrub-times" style={{ marginTop: 6 }}>
              <span>{fmtClock(playhead)}</span>
              <span>/</span>
              <span>{fmtClock(duration)}</span>
            </div>
          </div>

          <div className="transport" style={{ marginTop: 12 }}>
            <button className="iconbtn" onClick={() => seek(0)} title="к началу">{Icons.prev}</button>
            <button className="iconbtn" onClick={() => seek(Math.max(0, playhead - 5))} title="−5с">{Icons.back}</button>
            <button className="iconbtn iconbtn-primary big-play" onClick={togglePlay}>{playing ? Icons.pause : Icons.play}</button>
            <button className="iconbtn" onClick={() => seek(Math.min(duration, playhead + 5))} title="+5с">{Icons.fwd}</button>
            <button className="iconbtn" onClick={() => seek(duration)} title="к концу">{Icons.next}</button>
          </div>
        </section>

        {/* RIGHT: marker controls + create */}
        <aside className="new-clip-controls">
          <div className="ed-card">
            <div className="ed-card-head">
              <div className="ed-card-title">Начало клипа</div>
              <span className="ed-card-sub" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(markIn)}</span>
            </div>
            <MarkerRow
              label="HH:MM:SS"
              value={markIn}
              onChange={(v) => setMarkIn(Math.max(0, Math.min(markOut - 1, v)))}
              onStep={(d) => setMarkIn(Math.max(0, Math.min(markOut - 1, markIn + d)))}
            />
            <div className="ed-card-actions" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-dark" onClick={setStartHere}>
                ⊙ поставить здесь · {fmtClock(playhead)}
              </button>
              <button className="btn btn-ghost" onClick={() => seek(markIn)} title="перейти к началу">→ к началу</button>
            </div>
          </div>

          <div className="ed-card">
            <div className="ed-card-head">
              <div className="ed-card-title">Конец клипа</div>
              <span className="ed-card-sub" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(markOut)}</span>
            </div>
            <MarkerRow
              label="HH:MM:SS"
              value={markOut}
              onChange={(v) => setMarkOut(Math.max(markIn + 1, Math.min(duration, v)))}
              onStep={(d) => setMarkOut(Math.max(markIn + 1, Math.min(duration, markOut + d)))}
            />
            <div className="ed-card-actions" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-dark" onClick={setEndHere}>
                ⊙ поставить здесь · {fmtClock(playhead)}
              </button>
              <button className="btn btn-ghost" onClick={() => seek(markOut)} title="перейти к концу">→ к концу</button>
            </div>
          </div>

          <div className="ed-card">
            <div className="ed-card-head">
              <div className="ed-card-title">Длительность</div>
              <span className="ed-card-sub" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDur(clipDur)}</span>
            </div>
            <div className="ed-card-hint" style={{ fontSize: 12 }}>
              для Reels оптимально 30–90 секунд, максимум 3 минуты
            </div>
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-outline btn-full" onClick={previewRange}>
                {Icons.play} проиграть отрезок
              </button>
            </div>
          </div>

          <div className="ed-card">
            <div className="ed-card-head">
              <div className="ed-card-title">Название (необязательно)</div>
            </div>
            <input
              type="text"
              className="text-input"
              placeholder="например: Tres reglas para escuchar"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6, marginTop: 6 }}
            />
            <div className="ed-card-hint" style={{ marginTop: 6, fontSize: 12 }}>
              если пусто — будет «Manual clip N»
            </div>
          </div>

          <button
            className="btn btn-publish btn-full publish-cta"
            onClick={create}
            disabled={creating || clipDur < 2 || markOut <= markIn}
            style={{ marginTop: 8 }}
          >
            {creating ? (<><span className="spinner" /> создаю…</>) : 'создать клип →'}
          </button>
          <div className="muted small" style={{ textAlign: 'center', marginTop: 8 }}>
            субтитры подтянутся автоматически из транскрипта
          </div>
        </aside>
      </div>
    </div>
  );
}

function MarkerRow({ value, onChange, onStep }) {
  const [draft, setDraft] = useState(fmtClock(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(fmtClock(value)); }, [value, focused]);

  const commit = () => {
    const parsed = parseClock(draft);
    if (parsed != null) onChange(parsed);
    else setDraft(fmtClock(value));
    setFocused(false);
  };

  return (
    <div className="trim-row">
      <button className="trim-step" onClick={() => onStep(-10)} title="−10с">−10</button>
      <button className="trim-step" onClick={() => onStep(-1)} title="−1с">−1</button>
      <input className="trim-time"
             value={draft}
             onChange={(e) => setDraft(e.target.value)}
             onFocus={(e) => { setFocused(true); e.target.select(); }}
             onBlur={commit}
             onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(fmtClock(value)); e.target.blur(); } }} />
      <button className="trim-step" onClick={() => onStep(1)} title="+1с">+1</button>
      <button className="trim-step" onClick={() => onStep(10)} title="+10с">+10</button>
    </div>
  );
}
